import { useState } from 'react';
import { Header } from './Header';
import { Send, Loader2, CheckCircle2, AlertCircle, ChevronDown } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  organization: string;
  role: string;
  phone: string;
  help_description: string;
}

const countryCodes = [
  { code: '+44', country: 'United Kingdom' },
  { code: '+353', country: 'Ireland' },
  { code: '+33', country: 'France' },
  { code: '+49', country: 'Germany' },
  { code: '+34', country: 'Spain' },
  { code: '+39', country: 'Italy' },
  { code: '+31', country: 'Netherlands' },
  { code: '+32', country: 'Belgium' },
  { code: '+352', country: 'Luxembourg' },
  { code: '+41', country: 'Switzerland' },
  { code: '+43', country: 'Austria' },
  { code: '+46', country: 'Sweden' },
  { code: '+47', country: 'Norway' },
  { code: '+45', country: 'Denmark' },
  { code: '+358', country: 'Finland' },
  { code: '+354', country: 'Iceland' },
  { code: '+48', country: 'Poland' },
  { code: '+420', country: 'Czech Republic' },
  { code: '+421', country: 'Slovakia' },
  { code: '+36', country: 'Hungary' },
  { code: '+40', country: 'Romania' },
  { code: '+30', country: 'Greece' },
  { code: '+351', country: 'Portugal' },
  { code: '+359', country: 'Bulgaria' },
  { code: '+385', country: 'Croatia' },
  { code: '+386', country: 'Slovenia' },
  { code: '+372', country: 'Estonia' },
  { code: '+371', country: 'Latvia' },
  { code: '+370', country: 'Lithuania' },
  { code: '+1', country: 'United States' },
  { code: '+1', country: 'Canada' },
  { code: '+52', country: 'Mexico' },
  { code: '+81', country: 'Japan' },
  { code: '+82', country: 'South Korea' },
  { code: '+86', country: 'China' },
  { code: '+852', country: 'Hong Kong' },
  { code: '+886', country: 'Taiwan' },
  { code: '+65', country: 'Singapore' },
  { code: '+60', country: 'Malaysia' },
  { code: '+66', country: 'Thailand' },
  { code: '+84', country: 'Vietnam' },
  { code: '+91', country: 'India' },
  { code: '+92', country: 'Pakistan' },
  { code: '+971', country: 'UAE' },
  { code: '+972', country: 'Israel' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+974', country: 'Qatar' },
  { code: '+61', country: 'Australia' },
  { code: '+64', country: 'New Zealand' },
  { code: '+55', country: 'Brazil' },
  { code: '+54', country: 'Argentina' },
  { code: '+56', country: 'Chile' },
  { code: '+57', country: 'Colombia' },
  { code: '+51', country: 'Peru' },
  { code: '+58', country: 'Venezuela' },
  { code: '+27', country: 'South Africa' },
  { code: '+20', country: 'Egypt' },
  { code: '+234', country: 'Nigeria' },
  { code: '+254', country: 'Kenya' },
  { code: '+212', country: 'Morocco' },
  { code: '+216', country: 'Tunisia' },
].sort((a, b) => a.country.localeCompare(b.country));

export function RegisterForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    organization: '',
    role: '',
    phone: '',
    help_description: '',
  });
  const [selectedCountryCode, setSelectedCountryCode] = useState('+44');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const submissionData = {
      ...formData,
      phone: selectedCountryCode + ' ' + formData.phone.replace(/^\+\d+\s*/, ''),
    };

    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData),
      });
      if (!response.ok) throw new Error('Registration request failed');

      setSubmitStatus('success');
      setFormData({ name: '', email: '', organization: '', role: '', phone: '', help_description: '' });
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'countryCode') {
      setSelectedCountryCode(value);
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-20 w-full">
        <div className="grid grid-cols-12 gap-x-6">
          <div className="col-span-12 lg:col-span-5">
            <p className="eyebrow text-primary mb-6">Register interest</p>
            <h1 className="display-lg text-white mb-6">Let's talk.</h1>
            <p className="text-lg text-white/65 leading-snug max-w-md mb-10">
              Tell us about your programme. We'll come back to you within two working days.
            </p>
            <div className="space-y-4 text-sm text-white/55 max-w-md">
              <p className="eyebrow text-white/40">What happens next</p>
              <ul className="space-y-3">
                {[
                  'A senior engineer reads your brief. No salesperson triage.',
                  'We propose a no-cost scoping call within 48 hours.',
                  'If we are not the right fit, we will tell you and recommend who is.',
                ].map((line, i) => (
                  <li key={line} className="flex items-baseline gap-3">
                    <span className="font-mono text-primary text-xs">{String(i + 1).padStart(2, '0')}</span>
                    <span className="leading-relaxed">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7 mt-12 lg:mt-0">
            <form onSubmit={handleSubmit} className="space-y-5">
              <Field label="Name">
                <input type="text" name="name" required value={formData.name} onChange={handleChange} className="form-input" placeholder="Your full name" />
              </Field>

              <Field label="Work email">
                <input type="email" name="email" required value={formData.email} onChange={handleChange} className="form-input" placeholder="you@company.com" />
              </Field>

              <Field label="Organisation">
                <input type="text" name="organization" required value={formData.organization} onChange={handleChange} className="form-input" placeholder="Company" />
              </Field>

              <Field label="Role">
                <input type="text" name="role" required value={formData.role} onChange={handleChange} className="form-input" placeholder="Head of PMO" />
              </Field>

              <Field label="Phone">
                <div className="flex gap-2">
                  <div className="relative">
                    <select
                      name="countryCode"
                      value={selectedCountryCode}
                      onChange={handleChange}
                      className="form-input appearance-none pr-9"
                      style={{ width: 'auto' }}
                    >
                      {countryCodes.map(({ code, country }) => (
                        <option key={`${code}-${country}`} value={code} className="bg-[#1B2540]">
                          {code} {country}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50 pointer-events-none" />
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input flex-1"
                    placeholder="7700 900000"
                  />
                </div>
              </Field>

              <Field label="How can Movar Reply help?">
                <textarea
                  name="help_description"
                  required
                  value={formData.help_description}
                  onChange={handleChange}
                  rows={5}
                  className="form-input resize-none"
                  placeholder="Tell us about your programme, the problem, the timeline."
                />
              </Field>

              <p className="text-xs text-white/40 leading-relaxed">
                By submitting this form, you consent to Movar Reply contacting you regarding your
                enquiry. We may also send occasional updates about our work. You can unsubscribe
                any time.
              </p>

              {submitStatus && (
                <div
                  className={`p-4 flex items-center gap-3 border ${
                    submitStatus === 'success'
                      ? 'border-green-500/30 text-green-400 bg-green-500/5'
                      : 'border-red-500/30 text-red-400 bg-red-500/5'
                  }`}
                >
                  {submitStatus === 'success' ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      <p className="text-sm">Thank you. We'll be in touch shortly.</p>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <p className="text-sm">Something went wrong. Please try again.</p>
                    </>
                  )}
                </div>
              )}

              <button type="submit" disabled={isSubmitting} className="btn-primary disabled:opacity-50">
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Sending</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Send</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
          <p className="eyebrow text-white/40">&copy; 2026 Movar Reply</p>
          <p className="text-sm text-white/45 italic">big enough to deliver, small enough to care</p>
          <p className="eyebrow text-white/30 md:ml-auto">A Reply Group company</p>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="eyebrow text-white/60 mb-2 block">{label}</span>
      {children}
    </label>
  );
}
